import { useAuth } from '../services/contexts/authContext';
import { STATIC_USER_ROLES } from '../utils/enums';

/**
 * Custom hook for checking dynamic RBAC permissions.
 * 
 * Usage:
 *   const { can, hasPageAccess } = usePermissions();
 *   
 *   // Check CRUD permission on a page
 *   if (can('FX4', 'C')) { ... } // Can create branches?
 *   
 *   // Check if user has any access to a page
 *   if (hasPageAccess('FX4')) { ... } // Can see branches page?
 */
export function usePermissions() {
    const { userDetails } = useAuth();

    const isSuperAdmin = userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN;

    // Admin-side staff roles that use dynamic permissions
    const isStaff = userDetails?.role === STATIC_USER_ROLES.STAFF;

    // Legacy roles that haven't been migrated yet — treat as having full access
    const isLegacyAdmin = [
        STATIC_USER_ROLES.ADMIN,
        STATIC_USER_ROLES.MANAGER,
        STATIC_USER_ROLES.EMPLOYEE,
    ].includes(userDetails?.role);

    const permissions = userDetails?.dynamicRole?.permissions || [];

    /**
     * Check if user has a specific CRUD permission on a route.
     * @param {string} routeId - Route ID from routes.js e.g., 'FX4'
     * @param {string} operation - 'C', 'R', 'U', or 'D'
     * @returns {boolean}
     */
    const can = (routeId, operation) => {
        if (isSuperAdmin || isLegacyAdmin) return true;
        if (!isStaff) return false;
        return permissions.includes(`${routeId}:${operation}`);
    };

    /**
     * Check if user has access to a page (route ID is in permissions).
     * @param {string} routeId - Route ID from routes.js e.g., 'FX4'
     * @returns {boolean}
     */
    const hasPageAccess = (routeId) => {
        if (isSuperAdmin || isLegacyAdmin) return true;
        if (!isStaff) return false;
        return permissions.includes(routeId);
    };

    /**
     * Check if user is admin-side (staff, legacy admin, or super admin).
     * Used to determine which sidebar/layout to show.
     */
    const isAdminSide = isSuperAdmin || isStaff || isLegacyAdmin;

    return {
        can,
        hasPageAccess,
        permissions,
        isSuperAdmin,
        isStaff,
        isLegacyAdmin,
        isAdminSide,
    };
}
